'use client';

import { motion, useMotionValueEvent, useScroll } from 'motion/react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { TypedLocale } from 'payload';
import { useEffect, useLayoutEffect, useState } from 'react';

import { useBackgroundColor } from '@/contexts/BackgroundColorContext';
import { useFrontPageEffect } from '@/contexts/FrontPageEffectContext';
import type { Header } from '@/payload-types';
import { cn } from '@/utils/cn';
import { COLOR_MAP } from '@/utils/colorFunctions';
import { getDictionary } from '@/utils/getDictionary';

import styles from './Header.module.scss';
import { Navigation } from './Navigation';

export function HeaderClient({
  header,
  locale,
}: {
  header: Header;
  locale: TypedLocale;
}) {
  const pathname = usePathname();
  const { scrollY } = useScroll();
  const { backgroundColor } = useBackgroundColor();
  const { videoLoaded } = useFrontPageEffect();
  const isFrontPage = pathname === '/';
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [heroBottom, setHeroBottom] = useState<number | null>(null);
  const [initialBg, setInitialBg] = useState<string | null>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const t = getDictionary(locale);

  // SSR-safe DOM measurement
  useLayoutEffect(() => {
    if (isFrontPage) {
      setInitialBg('rgba(255, 255, 255, 0.0)');
      return;
    }

    const heroEl = document.querySelector('[data-background-color][data-hero]');
    if (heroEl instanceof HTMLElement) {
      const rect = heroEl.getBoundingClientRect();
      const bottom = rect.top + window.scrollY + rect.height;
      setHeroBottom(bottom);

      const bgColorKey = heroEl.dataset.backgroundColor;
      const resolved = bgColorKey
        ? COLOR_MAP[bgColorKey] || '#ffffff'
        : '#ffffff';
      setInitialBg(resolved);
    } else {
      // Fallback if no hero
      setInitialBg('#ffffff');
    }
  }, [isFrontPage]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.matchMedia('(max-width: 1279px)').matches);
    };

    checkMobile();

    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Trigger animation after hero scroll
  useMotionValueEvent(scrollY, 'change', (latest) => {
    if (isFrontPage) {
      setShouldAnimate(latest > (isMobile ? 1 : 100));
    } else if (heroBottom !== null) {
      setShouldAnimate(latest >= heroBottom);
    }
  });

  const navigation = Array.isArray(header.navigation)
    ? header.navigation
    : header.navigation?.[locale] || [];

  return (
    <motion.header
      key={initialBg ?? 'fallback'}
      className={cn(styles.header, mobileNavOpen && styles.mobileNavOpen)}
      animate={shouldAnimate ? { backgroundColor } : {}}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      initial={{
        backgroundColor: initialBg ?? 'rgba(255, 255, 255, 0.0)',
      }}
      style={{ willChange: 'background-color' }}
    >
      <div className={cn('container', styles.headerInner)}>
        <Link
          href={`/${locale !== 'da' ? locale : ''}`}
          className={cn(
            styles.logo,
            isFrontPage && styles.isFrontPage,
            isFrontPage && videoLoaded && styles.videoLoaded
          )}
          aria-label={t('goToFrontpage')}
        >
          <svg
            width='130'
            height='14'
            viewBox='0 0 130 14'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              fill='currentColor'
              d='M2.39684 0.721088C2.75927 0.721088 2.91105 0.823493 3.04873 1.28978L5.55094 9.20045C5.7954 10.1231 6.1588 10.6166 6.80193 10.6166H6.83209C7.47546 10.6166 7.83862 10.1231 8.08332 9.20045L10.5848 1.28978C10.7227 0.823493 10.8747 0.721088 11.2367 0.721088H13.1313C13.2114 0.712551 13.2924 0.725266 13.366 0.757953C13.4397 0.79064 13.5034 0.842147 13.5508 0.907291C13.5982 0.972435 13.6276 1.04892 13.6361 1.12904C13.6446 1.20917 13.6318 1.29011 13.599 1.36373L10.8567 9.86036L10.8104 9.99545C10.0396 12.2422 9.6025 13.5165 7.2349 13.5165H6.39936C4.03141 13.5165 3.59426 12.2419 2.82351 9.99443L2.77751 9.86036L0.0347342 1.36373C0.00097499 1.27932 -0.0084192 1.18713 0.00761999 1.09765C0.0236592 1.00816 0.064488 0.924976 0.125463 0.857546C0.174816 0.809054 0.234039 0.771765 0.299099 0.748217C0.364159 0.72467 0.433528 0.715417 0.502486 0.721088H2.39684ZM33.4136 0.798437C33.8008 0.798437 33.9816 0.967733 33.9816 1.33089V2.99903C33.9938 3.07637 33.9876 3.15552 33.9632 3.22995C33.9389 3.30439 33.8973 3.37198 33.8417 3.42716C33.7862 3.48234 33.7183 3.52353 33.6437 3.54733C33.5691 3.57114 33.4899 3.57688 33.4126 3.56408H26.7367C25.805 3.56408 25.3322 3.93794 25.3322 4.67447V5.83935H29.8239C29.9893 5.8259 30.1538 5.8758 30.2838 5.97897C30.3361 6.03606 30.3761 6.10334 30.4012 6.17658C30.4263 6.24983 30.4361 6.32747 30.4298 6.40464V8.07254C30.4298 8.43595 30.2371 8.60524 29.8241 8.60524H25.3322V9.68061C25.3322 10.4171 25.8158 10.791 26.7697 10.791H33.4126C33.4899 10.7782 33.5691 10.784 33.6438 10.8078C33.7184 10.8316 33.7863 10.8728 33.8419 10.928C33.8975 10.9832 33.9391 11.0508 33.9635 11.1253C33.9878 11.1997 33.9941 11.2789 33.9818 11.3563V13.0244C33.9877 13.0972 33.9786 13.1704 33.955 13.2395C33.9314 13.3087 33.894 13.3722 33.8449 13.4263C33.7227 13.5228 33.5685 13.5694 33.4134 13.5569H26.0884C23.6285 13.5569 22.4325 12.3078 22.4325 9.73826V4.74769C22.4325 2.09029 23.6176 0.798437 26.0556 0.798437H33.4136ZM108.931 1.33113C108.931 0.967733 108.751 0.798437 108.364 0.798437L101.006 0.798681C98.5675 0.798681 97.3824 2.09077 97.3824 4.74793V9.73801C97.3824 12.3078 98.5782 13.5569 101.038 13.5569H108.364C108.519 13.5695 108.673 13.5229 108.795 13.4263C108.844 13.3722 108.881 13.3086 108.905 13.2395C108.928 13.1704 108.937 13.0972 108.931 13.0244V11.3563C108.944 11.279 108.937 11.1999 108.913 11.1254C108.889 11.051 108.847 10.9834 108.792 10.9282C108.736 10.873 108.668 10.8318 108.594 10.8079C108.519 10.7841 108.44 10.7783 108.363 10.791H101.72C100.765 10.791 100.282 10.4171 100.282 9.68061V4.67472C100.282 3.93794 100.755 3.56408 101.687 3.56408H108.363C108.44 3.57685 108.519 3.57108 108.594 3.54726C108.668 3.52343 108.736 3.48223 108.792 3.42705C108.847 3.37187 108.889 3.30428 108.913 3.22987C108.937 3.15545 108.944 3.07634 108.931 2.99903V1.33113ZM76.0761 0.798432C76.1533 0.785818 76.2324 0.791712 76.3068 0.815626C76.3813 0.839541 76.449 0.880794 76.5043 0.93599C76.5597 0.991185 76.6012 1.05875 76.6254 1.13311C76.6496 1.20747 76.6557 1.28651 76.6434 1.36372V2.99903C76.6558 3.07624 76.6497 3.1553 76.6255 3.22968C76.6014 3.30407 76.5599 3.37164 76.5045 3.42683C76.4491 3.48202 76.3813 3.52325 76.3069 3.54712C76.2324 3.57098 76.1533 3.57679 76.0761 3.56408H71.9148V12.9561C71.9148 13.3659 71.736 13.5569 71.3517 13.5569H69.623C69.2386 13.5569 69.0599 13.3659 69.0599 12.9561V3.56408H64.889C64.4779 3.56408 64.2868 3.38481 64.2868 2.99903V1.36372C64.2868 0.978187 64.4774 0.798432 64.8868 0.798432H76.0761ZM88.1779 1.33308C88.1779 0.968706 87.9884 0.798437 87.582 0.798437H85.919C85.5123 0.798437 85.323 0.968463 85.323 1.33308V12.9563C85.323 13.3659 85.5018 13.5571 85.8861 13.5571H87.582C87.9884 13.5571 88.1779 13.3869 88.1779 13.022V1.33308ZM55.947 12.8739L54.5399 8.51451C55.4253 7.91979 55.8643 6.86048 55.8689 5.31639V4.7545C55.8689 2.09296 54.6851 0.798437 52.249 0.798437H44.7888C44.3795 0.798437 44.1888 0.968706 44.1888 1.33332V12.9566C44.1888 13.3659 44.3678 13.5569 44.7519 13.5569H46.4806C46.8649 13.5569 47.0437 13.3679 47.0437 12.9622V9.14207H51.729L52.9325 12.9476C53.0705 13.4146 53.2222 13.5165 53.5849 13.5165H55.4793C55.5593 13.5249 55.6402 13.5121 55.7138 13.4793C55.7873 13.4466 55.851 13.3951 55.8984 13.33C55.9458 13.2649 55.9752 13.1885 55.9837 13.1085C55.9923 13.0284 55.9796 12.9475 55.947 12.8739ZM53.014 5.24633C53.0104 5.996 52.5027 6.32535 51.5054 6.32535H47.0439V3.56408H51.5711C52.5283 3.56408 53.014 3.94743 53.014 4.70196V5.24633ZM127.218 4.37724L129.961 12.8734C129.993 12.947 130.006 13.0279 129.997 13.108C129.989 13.188 129.96 13.2645 129.912 13.3296C129.865 13.3947 129.801 13.4463 129.728 13.479C129.654 13.5118 129.573 13.5246 129.493 13.5163H127.598C127.236 13.5163 127.084 13.4141 126.947 12.9471L126.263 10.7864H120.094L119.41 12.9471C119.272 13.4141 119.121 13.5163 118.758 13.5163H116.864C116.784 13.5247 116.703 13.5119 116.63 13.4791C116.556 13.4464 116.492 13.3949 116.445 13.3297C116.398 13.2646 116.368 13.1881 116.36 13.108C116.351 13.0279 116.364 12.947 116.397 12.8734L119.139 4.37699L119.186 4.24078C119.956 1.99447 120.393 0.721089 122.761 0.721089H123.596C125.964 0.721089 126.401 1.99553 127.172 4.24381L127.218 4.37724ZM121.912 5.0369L120.968 8.02098L125.388 8.02074L124.445 5.0369C124.2 4.11454 123.837 3.62052 123.194 3.62052H123.163C122.52 3.62052 122.157 4.11454 121.912 5.0369Z'
            />
          </svg>
        </Link>
        <Navigation
          navigation={navigation}
          mobileNavigation={header?.mobileNavigation}
          mobileNavOpen={mobileNavOpen}
          setMobileNavOpen={setMobileNavOpen}
          locale={locale}
        />
      </div>
    </motion.header>
  );
}
